import React, { useCallback, useEffect, useState } from "react";
import { promoReqData } from "../../../data/settings/promo/promoData";
import { MenuItem, Pagination, TextField } from "@mui/material";
import { createPromoCode } from "../../../controllers/settings/promo/createPromoCode";
import { useDispatch } from "react-redux";
import { setAlertMsg } from "../../../redux/reducers/modal/snackBarSlice";
import { getPromoCodes } from "../../../controllers/settings/promo/getPromoCodes";
import {
  setModal,
  setModalComp,
} from "../../../redux/reducers/modal/modalSlice";
import DotMenu from "../../../components/mini/DotMenu";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import RemovePromoModal from "./RemovePromoModal";
import MultipleSelect from "../../../components/form/MultiSelect";
import { capitalize } from "../../../features/utils/capitalize";
import { Cancel, Check } from "@mui/icons-material";

function ActionCol({ params, reload }) {
  const [anchorEl, setAnchorEl] = useState();
  const dispatch = useDispatch();

  function handleRemove() {
    setAnchorEl(null);
    dispatch(setModal(true));
    let id = params.id;
    console.log("params: ", params);
    dispatch(setModalComp(<RemovePromoModal id={id} returnData={reload} />));
  }
  return (
    <div className="flex justify-between w-full relative">
      <DotMenu anchor={anchorEl} setAnchor={setAnchorEl} parentControl={true}>
        <MenuItem onClick={handleRemove}>Remove</MenuItem>
      </DotMenu>
    </div>
  );
}

const filterItems = [
  { items: [{ id: 0, field: "status", operator: "", value: "" }] },
  { items: [{ id: 1, field: "status", operatorValue: "is", value: "paid" }] },
  {
    items: [{ id: 2, field: "status", operatorValue: "is", value: "pending" }],
  },
  {
    items: [
      { id: 2, field: "status", operatorValue: "is", value: "cancelled" },
    ],
  },
  { items: [{ id: 2, field: "status", operatorValue: "is", value: "refund" }] },
];

const initialErrors = {
  code: "",
  expiryDate: "",
  discount: "",
  usedFor: "",
  userType: "",
};

const initialTouched = {
  code: false,
  expiryDate: false,
  discount: false,
  usedFor: false,
  userType: false,
};

function formatUserTypeOption(value) {
  return {
    label: capitalize(value.replace(/_/g, " ")),
    value,
  };
}

function getInitialFormData() {
  return {
    ...promoReqData,
    userType: promoReqData.userType.map(formatUserTypeOption),
    discount: 0,
    type: "Amount",
  };
}

function getClearedFormData() {
  return {
    ...promoReqData,
    userType: [],
    discount: "",
    type: "Amount",
  };
}

export default function Index() {
  const [datas, setDatas] = useState([]);
  const [data, setData] = useState(getInitialFormData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(initialErrors);
  const [touched, setTouched] = useState(initialTouched);
  const dispatch = useDispatch();

  const [filter, setFilter] = useState(filterItems[0]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const fetchData = useCallback(async () => {
    const res = await getPromoCodes();
    return res;
  }, []);
  useEffect(() => {
    load();
  }, [fetchData]);
  async function load() {
    const res = await fetchData();
    if (res.return) setDatas(res.data);
  }

  function validateField(fieldName, formData) {
    switch (fieldName) {
      case "code":
        return formData.code.trim() ? "" : "Code name is required.";
      case "expiryDate":
        if (!formData.expiryDate) return "Expiry date is required.";
        if (moment(formData.expiryDate).isBefore(moment().startOf("day")))
          return "Expiry date cannot be in the past.";
        return "";
      case "discount": {
        if (formData.discount === "" || formData.discount === null)
          return `Discount ${formData.type.toLowerCase()} is required.`;

        const discountValue = Number(formData.discount);
        if (Number.isNaN(discountValue))
          return "Discount must be a valid number.";
        if (discountValue <= 0) return "Discount must be greater than 0.";
        if (formData.type === "Percentage" && discountValue > 100)
          return "Percentage discount cannot exceed 100.";
        return "";
      }
      case "usedFor":
        return formData.usedFor ? "" : "Please select what this promo is for.";
      case "userType":
        return Array.isArray(formData.userType) && formData.userType.length > 0
          ? ""
          : "Please select at least one user type.";
      default:
        return "";
    }
  }

  function validateForm(formData) {
    const nextErrors = Object.keys(initialErrors).reduce((acc, key) => {
      acc[key] = validateField(key, formData);
      return acc;
    }, {});

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  }

  function handleBlur(fieldName) {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    setErrors((prev) => ({
      ...prev,
      [fieldName]: validateField(fieldName, data),
    }));
  }

  function updateField(fieldName, value) {
    const nextData = { ...data, [fieldName]: value };
    setData(nextData);

    if (touched[fieldName]) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: validateField(fieldName, nextData),
      }));
    }

    if (fieldName === "type" && touched.discount) {
      setErrors((prev) => ({
        ...prev,
        discount: validateField("discount", nextData),
      }));
    }
  }

  const columns = [
    {
      field: "code",
      headerName: "Code Name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value) return null;
        return <div className="flex gap-4 items-center">{params.value}</div>;
      },
    },
    {
      field: "discountPercentage",
      headerName: "%",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        return (
          <div className="flex justify-between w-full relative">
            <p
              className="flex-1 overflow-hidden overflow-ellipsis"
              title={params.value}
            >
              {params.value || "-"}
            </p>
          </div>
        );
      },
    },
    {
      field: "discountAmount",
      headerName: "Amount",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        return (
          <div className="flex justify-between w-full relative">
            <p
              className="flex-1 overflow-hidden overflow-ellipsis"
              title={params.value}
            >
              {params.value || "-"}
            </p>
          </div>
        );
      },
    },
    {
      field: "expiryDate",
      headerName: "Expiry Date",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params.value) return null;

        return (
          <div className="flex justify-between w-full relative">
            <p
              className="flex-1 overflow-hidden overflow-ellipsis"
              title={params.value}
            >
              {moment(params.value).format("YYYY-MM-DD")}
            </p>
          </div>
        );
      },
    },
    {
      field: "updatedDate",
      headerName: "Updated Date",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        // if(!params.value)
        // return null;

        return (
          <div className="flex justify-between w-full relative">
            <p
              className="flex-1 overflow-hidden overflow-ellipsis"
              title={params.value}
            >
              {moment(params.value).format("YYYY-MM-DD")}
            </p>
          </div>
        );
      },
    },
    {
      field: "multipleUsage",
      headerName: "Multiple Usage",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        // if(!params.value)
        // return null;

        return (
          <div className="flex justify-between w-full relative">
            <p
              className="flex-1 overflow-hidden overflow-ellipsis"
              title={params.value}
            >
              {params.value ? (
                <Check className="text-green-500" />
              ) : (
                <Cancel className="text-red-500" />
              )}
            </p>
          </div>
        );
      },
    },
    {
      field: "usedFor",
      headerName: "Used For",
      renderCell: (params) => {
        let usedFor = params.value || [];
        if (typeof usedFor === "string") usedFor = [usedFor];

        return (
          <div className="flex justify-between w-full relative">
            <p
              className="flex-1 overflow-hidden overflow-ellipsis"
              title={usedFor.join(", ")}
            >
              {usedFor.join(", ")}
            </p>
          </div>
        );
      },
    },
    {
      field: "userType",
      headerName: "For User",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        // if(!params.value)
        // return null;

        let userTypes = params.value || [];
        return (
          <div className="flex justify-between w-full relative">
            <p
              className="flex-1 overflow-hidden overflow-ellipsis"
              title={userTypes.join(", ")}
            >
              {userTypes.join(", ")}
            </p>
          </div>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        // if(!params.value)
        // return null;

        return <ActionCol reload={load} params={params} />;
      },
    },
  ];

  async function handleSubmit() {
    const normalizedData = {
      ...data,
      code: data.code.trim(),
    };

    setTouched(
      Object.keys(initialTouched).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}),
    );

    if (!validateForm(normalizedData))
      return dispatch(setAlertMsg(["error", "Please fill required fields!"]));

    const discountValue = Number(normalizedData.discount);
    const temped = {
      ...normalizedData,
      discountAmount: normalizedData.type === "Amount" ? discountValue : 0,
      discountPercentage:
        normalizedData.type === "Percentage" ? discountValue : 0,
    };
    temped.userType = normalizedData.userType.map((obj) =>
      typeof obj === "string" ? obj : obj.value,
    );

    setLoading(true);
    const res = await createPromoCode(temped);
    setLoading(false);

    if (res.return) {
      load();
      setData(getClearedFormData());
      setErrors({ ...initialErrors });
      setTouched({ ...initialTouched });
      dispatch(setAlertMsg(["success", "Promo Code Created."]));
    } else dispatch(setAlertMsg(["error", "Failed to create promo code!"]));
  }

  const userTypes = [
    "CUSTOMER",
    // "API_USER",
    "SUB_AGENT",
    "CORPORATE",
    "CORPORATE_STAFF",
    // "CORPORATE_STAFF_2",
  ].map(formatUserTypeOption);

  return (
    <div className="bg-[#f3f3f3] p-4 w-full flex flex-col gap-4">
      <div className="bg-secondary rounded-lg p-4 flex gap-3 flex-wrap">
        <div className="w-full flex gap-6 flex-wrap items-center">
          <TextField
            label="Type"
            name="promotype"
            size="small"
            className="min-w-[100px]"
            select
            value={data.type}
            onChange={(ev) => updateField("type", ev.target.value)}
          >
            <MenuItem value="Percentage">Percentage</MenuItem>
            <MenuItem value="Amount">Amount</MenuItem>
          </TextField>
          <TextField
            select
            label="For"
            size="small"
            className="min-w-[150px]"
            required
            value={data.usedFor}
            onChange={(ev) => updateField("usedFor", ev.target.value)}
            onBlur={() => handleBlur("usedFor")}
            error={touched.usedFor && Boolean(errors.usedFor)}
            helperText={touched.usedFor ? errors.usedFor : ""}
          >
            <MenuItem value="FLIGHT">Flight</MenuItem>
            <MenuItem value="HOTEL">Hotel</MenuItem>
            <MenuItem value="TOUR">Tour</MenuItem>
            <MenuItem value="FLIGHT_HOTEL">Flight & Hotel</MenuItem>
            <MenuItem value="FLIGHT_TOUR">Flight & Tour</MenuItem>
            <MenuItem value="HOTEL_TOUR">Hotel & Tour</MenuItem>
            <MenuItem value="ALL">All</MenuItem>
          </TextField>
          <label className="flex gap-2 cursor-pointer items-center">
            <input
              type="checkbox"
              name="multipleUsage"
              checked={data.multipleUsage}
              onChange={(ev) =>
                setData({ ...data, multipleUsage: ev.target.checked })
              }
            />
            Multiple Usage
          </label>
          <div className="flex-1 flex justify-end">
            <MultipleSelect
              label="User Type"
              options={userTypes}
              value={data.userType}
              required
              error={touched.userType && Boolean(errors.userType)}
              helperText={touched.userType ? errors.userType : ""}
              onBlur={() => handleBlur("userType")}
              onChange={(val) => {
                const uniqueValues = val.filter(
                  (item, index, self) =>
                    index === self.findIndex((v) => v.value === item.value),
                );
                const nextData = {
                  ...data,
                  userType: uniqueValues,
                };
                setData(nextData);

                if (touched.userType) {
                  setErrors((prev) => ({
                    ...prev,
                    userType: validateField("userType", nextData),
                  }));
                }
              }}
            />
          </div>
        </div>
        <TextField
          label="Code Name"
          className="flex-1"
          name="codename"
          size="small"
          required
          value={data.code}
          onChange={(ev) => updateField("code", ev.target.value)}
          onBlur={() => handleBlur("code")}
          error={touched.code && Boolean(errors.code)}
          helperText={touched.code ? errors.code : ""}
        />
        <TextField
          label="Expiry Date"
          type="date"
          size="small"
          required
          value={data.expiryDate}
          onChange={(ev) => updateField("expiryDate", ev.target.value)}
          onBlur={() => handleBlur("expiryDate")}
          error={touched.expiryDate && Boolean(errors.expiryDate)}
          helperText={touched.expiryDate ? errors.expiryDate : ""}
          inputProps={{
            min: moment().format("YYYY-MM-DD"),
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          label={"Discount " + data.type}
          name="discount"
          size="small"
          required
          type="number"
          value={data.discount}
          onChange={(ev) => updateField("discount", ev.target.value)}
          onBlur={() => handleBlur("discount")}
          error={touched.discount && Boolean(errors.discount)}
          helperText={touched.discount ? errors.discount : ""}
          inputProps={{
            min: 0,
            step: data.type === "Percentage" ? 1 : 0.01,
            ...(data.type === "Percentage" ? { max: 100 } : {}),
          }}
        />
        <button className="btn2 flex gap-2" onClick={handleSubmit}>
          {loading ? <div className="load"></div> : null}
          Submit
        </button>
      </div>

      <div className="bg-secondary rounded-lg p-4">
        <DataGrid
          rows={datas}
          columns={columns}
          loading={loading}
          autoHeight
          hideFooter={false}
          // getRowId={(row) => row.id}
          // apiRef={apiRef}
          initialState={{
            sorting: {
              sortModel: [{ field: "updatedDate", sort: "desc" }],
            },
          }}
          filterModel={filter}
          onFilterModelChange={(newFilter) => setFilter(newFilter)}
          // slots={{pagination: Pagination}}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          // onCellEditStart={params => setRowId(params.id)}
          // hideFooterPagination
          checkboxSelection
        />
        <Pagination
          perPage={10}
          total={46}
          // apiRef={apiRef}
        />
      </div>
    </div>
  );
}
